import React, { useState } from "react";
import styles from "../styles/LegalPage.module.css";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";

const LegalPolicies = () => {
  const [activePolicy, setActivePolicy] = useState("privacy");

  const renderPrivacyPolicy = () => (
    <div>
      <h1>Privacy Policy</h1>
      <p><strong>Last updated:</strong> July 19, 2025</p>
      <p>Your privacy is important to us. This comprehensive policy explains how Vionex Labs collects, uses, and protects your information when you use our website and services. We are committed to maintaining the highest standards of data protection and transparency.</p>
      
      <h2>Information We Collect</h2>
      <ul>
        <li>Personal information you provide (name, email, phone number, company details)</li>
        <li>Usage data and analytics to improve our services and user experience</li>
        <li>Cookies and similar technologies for website functionality and personalization</li>
        <li>Communication records when you contact us for support or inquiries</li>
        <li>Device information including IP address, browser type, and operating system</li>
        <li>Location data (with your consent) for localized services</li>
      </ul>
      
      <h2>How We Use Information</h2>
      <ul>
        <li>To provide, maintain, and improve our digital services and solutions</li>
        <li>To communicate with you about projects, updates, and service announcements</li>
        <li>To ensure security, prevent fraud, and maintain compliance with legal requirements</li>
        <li>To analyze website usage patterns and optimize performance</li>
        <li>To send relevant marketing communications (with your explicit consent)</li>
        <li>To provide customer support and respond to your inquiries</li>
        <li>To develop new features and services based on user feedback</li>
      </ul>
      
      <h2>Information Sharing</h2>
      <ul>
        <li>We do not sell, rent, or trade your personal data to third parties</li>
        <li>We may share data with trusted service providers who assist in our operations</li>
        <li>We may disclose information if required by law or legal process</li>
        <li>Data may be shared with your consent or at your direction</li>
        <li>Anonymous, aggregated data may be used for research and analytics</li>
      </ul>
      
      <h2>Your Rights</h2>
      <ul>
        <li>Access, review, and update your personal data at any time</li>
        <li>Request deletion of your personal information (right to be forgotten)</li>
        <li>Opt out of marketing communications and promotional materials</li>
        <li>Request data portability and transfer to another service</li>
        <li>File complaints with relevant data protection authorities</li>
        <li>Withdraw consent for data processing where applicable</li>
      </ul>
      
      <h2>Data Security & Protection</h2>
      <p>We implement industry-standard security measures including encryption, secure servers, regular security audits, and access controls to protect your information against unauthorized access, alteration, disclosure, or destruction. Our team follows strict data handling protocols and receives regular security training.</p>
      
      <h2>Data Retention</h2>
      <p>We retain your personal information only as long as necessary to fulfill the purposes outlined in this policy, comply with legal obligations, resolve disputes, and enforce our agreements. When data is no longer needed, it is securely deleted or anonymized.</p>
      
      <p><strong>Contact us:</strong> For any privacy-related questions or concerns, please reach out to us at <a href="mailto:privacy@vionexlabs.com" className={styles.emailLink}>privacy@vionexlabs.com</a></p>
    </div>
  );

  const renderTermsOfService = () => (
    <div>
      <h1>Terms of Service</h1>
      <p><strong>Last updated:</strong> July 19, 2025</p>
      <p>These Terms of Service govern your use of Vionex Labs website, services, and digital solutions. By accessing or using our services, you agree to be bound by these terms. Please read them carefully.</p>
      
      <h2>Acceptance of Terms</h2>
      <p>By accessing and using our website, services, or engaging with Vionex Labs in any capacity, you accept and agree to be bound by the terms and provisions of this agreement. If you do not agree to these terms, please do not use our services.</p>
      
      <h2>Our Services</h2>
      <ul>
        <li>Custom web development and responsive design solutions</li>
        <li>Mobile application development for iOS and Android platforms</li>
        <li>Software consulting, architecture, and digital transformation</li>
        <li>UI/UX design and user experience optimization</li>
        <li>Digital marketing, SEO, and brand development services</li>
        <li>Cloud solutions, hosting, and technical support</li>
        <li>E-commerce development and online business solutions</li>
      </ul>
      
      <h2>User Responsibilities</h2>
      <ul>
        <li>Provide accurate, current, and complete information during registration</li>
        <li>Maintain the confidentiality of your account credentials and login information</li>
        <li>Use our services in accordance with applicable laws and regulations</li>
        <li>Respect intellectual property rights of Vionex Labs and third parties</li>
        <li>Refrain from uploading harmful, illegal, or inappropriate content</li>
        <li>Notify us immediately of any unauthorized use of your account</li>
        <li>Cooperate with our team during project development and implementation</li>
      </ul>
      
      <h2>Intellectual Property Rights</h2>
      <p>All content, features, functionality, designs, code, and materials on our website and in our services are owned by Vionex Labs and are protected by copyright, trademark, patent, and other intellectual property laws. You may not reproduce, distribute, or create derivative works without our explicit written consent.</p>
      
      <h2>Service Availability</h2>
      <p>While we strive to provide uninterrupted service, we cannot guarantee 100% uptime. We reserve the right to modify, suspend, or discontinue any part of our services with or without notice. We are not liable for any modifications, price changes, suspension, or discontinuance of services.</p>
      
      <h2>Payment Terms</h2>
      <ul>
        <li>Payment terms will be specified in individual project agreements</li>
        <li>All payments are due according to the agreed-upon schedule</li>
        <li>Late payments may incur additional charges as specified in contracts</li>
        <li>Refunds are handled according to our refund policy</li>
      </ul>
      
      <h2>Limitation of Liability</h2>
      <p>Vionex Labs shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or business interruption, arising from the use of our services, even if we have been advised of the possibility of such damages.</p>
      
      <h2>Indemnification</h2>
      <p>You agree to defend, indemnify, and hold harmless Vionex Labs and its affiliates from any claims, damages, losses, or expenses arising from your use of our services or violation of these terms.</p>
      
      <h2>Termination</h2>
      <p>We reserve the right to terminate or suspend access to our services at our sole discretion, without prior notice, for conduct that we believe violates these terms or is harmful to other users, us, or third parties, or for any other reason.</p>
      
      <h2>Governing Law</h2>
      <p>These terms shall be governed by and construed in accordance with the laws of India. Any disputes arising from these terms will be subject to the exclusive jurisdiction of courts in India.</p>
      
      <p><strong>Contact us:</strong> For questions about these terms, please contact us at <a href="mailto:legal@vionexlabs.com" className={styles.emailLink}>legal@vionexlabs.com</a></p>
    </div>
  );

  const renderCookiePolicy = () => (
    <div>
      <h1>Cookie Policy</h1>
      <p><strong>Last updated:</strong> July 19, 2025</p>
      <p>This Cookie Policy explains how Vionex Labs uses cookies and similar tracking technologies when you visit our website. We believe in transparency about how we collect and use data related to your use of our website.</p>
      
      <h2>What Are Cookies</h2>
      <p>Cookies are small text files that are placed on your computer, smartphone, or other device when you visit a website. They are widely used to make websites work more efficiently and provide information to website owners about user behavior and preferences.</p>
      
      <h2>Types of Cookies We Use</h2>
      
      <h3>Essential Cookies</h3>
      <ul>
        <li>Required for the website to function properly and provide core functionality</li>
        <li>Enable basic features like page navigation and access to secure areas</li>
        <li>Cannot be disabled without significantly affecting site functionality</li>
        <li>Include session management and security tokens</li>
      </ul>
      
      <h3>Performance & Analytics Cookies</h3>
      <ul>
        <li>Help us understand how visitors interact with our website</li>
        <li>Provide insights for improving user experience and site performance</li>
        <li>Collect anonymous information about page views, time spent, and user journeys</li>
        <li>Include Google Analytics, Hotjar, and similar analytics services</li>
        <li>Track conversion rates and goal completions</li>
      </ul>
      
      <h3>Functionality Cookies</h3>
      <ul>
        <li>Remember your preferences and settings between visits</li>
        <li>Enhance website functionality and personalization</li>
        <li>Store language preferences and accessibility settings</li>
        <li>Remember form data and user interface customizations</li>
      </ul>
      
      <h3>Marketing & Advertising Cookies</h3>
      <ul>
        <li>Track visitors across websites for advertising purposes</li>
        <li>Display relevant advertisements based on your interests</li>
        <li>Measure advertising campaign effectiveness and ROI</li>
        <li>Enable social media sharing and integration</li>
        <li>Support retargeting and remarketing campaigns</li>
      </ul>
      
      <h2>How We Use Cookies</h2>
      <ul>
        <li>To provide a personalized and optimized browsing experience</li>
        <li>To analyze website traffic and understand user behavior patterns</li>
        <li>To improve our services and develop new features</li>
        <li>To deliver targeted content and advertisements</li>
        <li>To measure the effectiveness of our marketing campaigns</li>
        <li>To prevent fraud and enhance website security</li>
      </ul>
      
      <h2>Managing Your Cookie Preferences</h2>
      <p>You have several options to control and manage cookies:</p>
      <ul>
        <li>Use our cookie consent banner to accept or reject non-essential cookies</li>
        <li>Modify your browser settings to block all or specific types of cookies</li>
        <li>Delete existing cookies stored on your device</li>
        <li>Set preferences for future website visits</li>
        <li>Use private/incognito browsing mode to prevent cookie storage</li>
        <li>Install browser extensions that block tracking cookies</li>
      </ul>
      
      <h2>Third-Party Cookies</h2>
      <p>We may use third-party services that place cookies on your device. These include:</p>
      <ul>
        <li>Google Analytics for comprehensive website analytics and reporting</li>
        <li>Social media platforms (Facebook, LinkedIn, Twitter) for content sharing</li>
        <li>Advertising networks for targeted advertising and retargeting</li>
        <li>Customer support tools like live chat and helpdesk systems</li>
        <li>Content delivery networks (CDNs) for improved website performance</li>
        <li>Payment processors for secure transaction handling</li>
      </ul>
      
      <h2>Cookie Duration</h2>
      <p>Cookies may be either "session" cookies (deleted when you close your browser) or "persistent" cookies (remain on your device for a set period). We use both types depending on their purpose, with persistent cookies typically lasting from 30 days to 2 years.</p>
      
      <h2>Updates to This Policy</h2>
      <p>We may update this Cookie Policy from time to time to reflect changes in our practices or for other operational, legal, or regulatory reasons. When we make changes, we will update the "Last updated" date and notify users through our website or email communications.</p>
      
      <p><strong>Contact us:</strong> For cookie-related questions or to exercise your rights, please contact us at <a href="mailto:privacy@vionexlabs.com" className={styles.emailLink}>privacy@vionexlabs.com</a></p>
    </div>
  );

  return (
    <>
      <Navbar />
      
      {/* Hero Section */}
      <div className={styles.heroSection}>
        <div className={styles.heroContent}>
          <h1 className={styles.heroTitle}>Legal Information</h1>
          <p className={styles.heroSubtitle}>
            Your privacy and legal rights are important to us. Read our comprehensive policies below.
          </p>
        </div>
        <div className={styles.heroDecoration}></div>
      </div>

      {/* Main Content */}
      <div className={styles.mainContainer}>
        <div className={styles.legalWrapper}>
          {/* Navigation Tabs */}
          <div className={styles.tabsContainer}>
            <div className={styles.tabsWrapper}>
              <button
                onClick={() => setActivePolicy("privacy")}
                className={`${styles.tabButton} ${activePolicy === "privacy" ? styles.activeTab : ""}`}
              >
                <span className={styles.tabIcon}>🔒</span>
                Privacy Policy
              </button>
              <button
                onClick={() => setActivePolicy("terms")}
                className={`${styles.tabButton} ${activePolicy === "terms" ? styles.activeTab : ""}`}
              >
                <span className={styles.tabIcon}>📋</span>
                Terms of Service
              </button>
              <button
                onClick={() => setActivePolicy("cookies")}
                className={`${styles.tabButton} ${activePolicy === "cookies" ? styles.activeTab : ""}`}
              >
                <span className={styles.tabIcon}>🍪</span>
                Cookie Policy
              </button>
            </div>
          </div>

          {/* Content Area */}
          <div className={styles.contentArea}>
            {activePolicy === "privacy" && renderPrivacyPolicy()}
            {activePolicy === "terms" && renderTermsOfService()}
            {activePolicy === "cookies" && renderCookiePolicy()}
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default LegalPolicies;
